/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React from 'react';
import {
  Controller,
  Control,
  FieldError,
  ValidationRule,
  FieldValues,
  FieldPath,
} from 'react-hook-form';

import { TextField } from '@material-ui/core';

type Rules = {
  required: boolean;
  pattern?: ValidationRule<RegExp> | undefined;
  maxLength?: number;
};

type Props<T extends FieldValues> = {
  inputType: FieldPath<T>;
  error?: FieldError | undefined;
  control: Control<T, object>;
  helperText?: string;
  placeholder?: string;
  rules?: Rules | undefined;
  minRows?: number;
  label?: string;
  disabled?: boolean;
};

export const InputField = <T extends FieldValues>({
  inputType,
  error,
  control,
  helperText,
  placeholder,
  label,
  rules,
  minRows = 0,
  disabled = false,
}: Props<T>) => {
  const inputLabel = label
    ? label
    : inputType.charAt(0).toLocaleUpperCase('en-US') + inputType.slice(1);

  return (
    <Controller
      name={inputType}
      control={control}
      rules={rules}
      render={({ field }) => (
        <TextField
          {...field}
          inputProps={{ 'aria-label': inputType, maxLength: rules?.maxLength }}
          InputLabelProps={{ shrink: field.value ? true : false }}
          required={rules?.required}
          margin="dense"
          multiline
          type="text"
          fullWidth
          label={inputLabel}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message ?? helperText}
          minRows={minRows}
          disabled={disabled}
        />
      )}
    />
  );
};
